import Chance from "@/Components/Chance"
import classNames from "classnames"
import { mapDesigns } from "@mono/mapDesigns"
import BoardCell from "./BoardCell"
import DiceArea from "./DiceArea"
import GameButtons from "./GameButtons"
import GameButtonsTop from "./GameButtonsTop"
import GameEndStatsButton from "./GameEndStatsButton"
import { getUserCreds, isMobile } from "@/utils/utils"
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { playDiceSound } from "@/utils/audioManager"
import { WrenchIcon } from "@heroicons/react/24/solid"
import StockModal from "@/Modals/StockModal"
import GameTimer from "@/Components/GameTimer"

//@ts-ignore
import { DicePhysics } from "@mono/custom-dice/src/DicePhysics.js"
import MoneyOnTable from "./MoneyOnTable"
import { useGameTimings } from "@/Hooks/useGameTimings"
import { useAppSelector, useAppDispatch } from "@/store/hooks"
import { setState } from "@/store/globalSlice"
import BoardCellWrapper from "./BoardCellWrapper"
import GameLogPanel from "./GameLogPanel"
import DecisionWaitBanner from "./DecisionWaitBanner"
import BoardPickerBanner from "./BoardPickerBanner"
import TurnsLeftBadge from "./TurnsLeftBadge"
import { allPieces } from "@/utils/data"
import { useTimerTickSound } from "@/Hooks/useTimerTickSound"

// Pip positions (3x3 grid cells, 0-indexed) for each die value 1-6.
const PIP_LAYOUT: Record<number, number[]> = {
  1: [4],
  2: [0, 8],
  3: [0, 4, 8],
  4: [0, 2, 6, 8],
  5: [0, 2, 4, 6, 8],
  6: [0, 2, 3, 5, 6, 8],
}

// Matches the 3D dice: premium red face with white pips.
const DieFace: React.FC<{ value: number }> = ({ value }) => (
  <div className="grid grid-cols-3 grid-rows-3 gap-0.5 bg-gradient-to-b from-[#e0282b] to-[#a50f12] rounded-md shadow-lg ring-1 ring-black/15 p-1.5 w-12 h-12 sm:w-14 sm:h-14">
    {Array.from({ length: 9 }).map((_, cell) => (
      <div key={cell} className="flex items-center justify-center">
        {PIP_LAYOUT[value]?.includes(cell) && (
          <span className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-white" />
        )}
      </div>
    ))}
  </div>
)

interface BoardProps {
  /**
   * Draw the board as a picture of itself rather than as a live one, for the
   * guide's copy of the screen (Components/GuideGameMock.tsx).
   *
   * Everything on the board that is a running machine rather than a rendering
   * of state has to stand down: the three.js dice box (a second one would
   * attach itself to the live board's container, since it finds it by id), the
   * dice sound, and the turn clock's tick. What is left is the same markup the
   * live board draws, plus a still pair of dice standing in for the physics
   * ones — those only exist mid-roll, and a diagram cannot roll.
   */
  preview?: boolean
}

const Board: React.FC<BoardProps> = ({ preview }) => {
  const constructionBanned = useAppSelector(
    (state) => state.global.myGame?.situation?.constructionBanned
  )
  const myGameId = useAppSelector((state) => state.global.myGame?.id)
  const turnRemainingForConstructionBan = useAppSelector(
    (state) => state.global.myGame?.situation?.turnRemainingForConstructionBan
  )
  const myEvents = useAppSelector(
    (state) => state.global.myGame?.situation?.events
  )
  const activeAuctionPropertyOrder = useAppSelector(
    (state) => state.global.myGame?.situation?.activeAuctionPropertyOrder
  )
  const gameOver = useAppSelector(
    (state) => state.global.myGame?.situation?.gameOver
  )
  const pendingDecision = useAppSelector(
    (state) => state.global.pendingDecision
  )
  const houseBuildMode = useAppSelector((state) => state.global.houseBuildMode)
  const boardPickerMode = useAppSelector(
    (state) => state.global.boardPickerMode
  )
  const isPortrait = useAppSelector((state) => state.global.isPortrait)
  const idleWarning = useAppSelector((state) => state.global.idleWarning)
  const mapId = useAppSelector((state) => state.global.myGame?.settings.mapId)
  const currentTurn = useAppSelector(
    (state) => state.global.myGame?.situation?.turn
  )
  const { turnTotalTime } = useGameTimings()
  const gamePlayers = useAppSelector((state) => state.global.myGame?.players)
  const situationPlayers = useAppSelector(
    (state) => state.global.myGame?.situation?.players
  )
  const isMyTurn = currentTurn === getUserCreds().email
  // The turn clock ticks only for whoever's turn it is, even though the timer
  // itself is on screen for everyone. Goes quiet whenever the server freezes the
  // turn timer — a decision, property management, a pause — since turnTimeLeft
  // stops moving. The hook watches that clock itself: reading the seconds into
  // a selector here would re-render all 40 cells once a second.
  useTimerTickSound(!preview && isMyTurn && !gameOver, "turnTimeLeft")
  const myOrder = situationPlayers?.find(
    (player) => player.id === getUserCreds().email
  )?.order
  // Waiting on a decision that isn't ours. Shown to the active player too: their
  // turn buttons are hidden while the game is frozen, so the slot is free.
  const showDecisionWait =
    !!pendingDecision && pendingDecision.playerOrder !== myOrder
  const activePlayer = situationPlayers?.find(
    (player) => player.id === currentTurn
  )
  const activeUser = gamePlayers?.find((user) => user.email === currentTurn)
  // The idle badge names whoever it is about, except to that player themselves —
  // they get told it is their own turn running out.
  const isIdlePlayerMe = !!idleWarning && idleWarning.playerOrder === myOrder
  const idleUser = gamePlayers?.find(
    (user) =>
      user.email ===
      situationPlayers?.find(
        (player) => player.order === idleWarning?.playerOrder
      )?.id
  )
  const activePiece = allPieces.find(
    (piece) => piece.id === activePlayer?.pieceId
  )
  const diceRefSmall = useRef<any>(null)
  const diceRefFull = useRef<any>(null)
  const mapDesign = mapDesigns.find((map) => map.id === mapId)
  const [diceAnimationRunning, setDiceAnimationRunning] = useState(false)
  // const pieceMovementEvent = state.myGame?.situation?.events.find(
  //    (event) => event.type === "piece-movement"
  // );
  //

  const initializeDiceBox = useCallback(
    (selector: string, ref: React.MutableRefObject<any>) => {
      if (ref.current) return
      const container = document.querySelector(selector) as HTMLElement | null
      if (!container) return

      // Custom three.js + cannon-es dice roller, imported as a standalone
      // component from the @mono/custom-dice package. The dice land on random
      // faces (physics-driven), so they're decorative — the authoritative server
      // roll is shown in the badge below.
      //
      // Premium red dice with white pips, built to NEVER wash toward pink. Pink
      // only comes from additive white specular (environment reflections +
      // clearcoat highlights). Diffuse shading is multiplicative, so on a pure
      // #ff0101 body (green/blue ~0) it can only deepen toward darker red, never
      // pink. So we keep shading (a baked top->bottom gradient for depth + soft
      // directional diffuse) and kill every white-specular source: no env
      // reflections, no clearcoat, matte surface, no tone-map desaturation.
      const Dice = new DicePhysics(container, {
        numDice: 2,
        laneSpacing: 3.0, // dice spawn apart; they collide so they never overlap
        dropHeight: 5, // higher drop -> more lively bounce/tumble
        material: "gradient", // baked depth, not reflection-based
        bodyColor: 0xff0101, // exact red
        pipColor: 0xffffff, // white pips
        pipRadius: 0.2,
        toneMapping: "none", // keep the red exact/saturated (no ACES wash)
        envMapIntensity: 0, // no studio reflections -> no white-on-red = no pink
        clearcoat: 0, // no glossy clearcoat highlight (a pink source)
        bodyRoughness: 0.85, // matte: kills specular hotspots
        keyLightIntensity: 1.1, // gentle directional shading (multiplicative, safe)
        ambientIntensity: 0.55,
        hemiIntensity: 0, // hemi sky color is bluish -> off, avoids any tint
        fillLightIntensity: 0,
        pipMetalness: 0,
        pipRoughness: 0.5,
      })
      Dice.onRollComplete = () => {
        setDiceAnimationRunning(false)
      }
      ref.current = Dice
    },
    []
  )

  useEffect(() => {
    // The guide's copy has no dice box to attach to, and both containers are
    // found by id — it would otherwise build its roller inside the live board's.
    if (preview) return
    initializeDiceBox("#dice-box-small", diceRefSmall)
    initializeDiceBox("#dice-box-full", diceRefFull)
    return () => {
      diceRefSmall.current?.dispose?.()
      diceRefFull.current?.dispose?.()
      diceRefSmall.current = null
      diceRefFull.current = null
    }
  }, [initializeDiceBox, preview])

  const diceRollEvent = myEvents?.find((event) => event.type === "dice-roll")
  // The authoritative roll values from the server. The 3D dice are cosmetic
  // (random physics), so these are what we actually display to the player.
  const [rolledDice, setRolledDice] = useState<number[] | null>(null)
  useEffect(() => {
    if (!myGameId || preview) return

    if (diceRollEvent) {
      playDiceSound().catch(console.error)
      setDiceAnimationRunning(true)
      setRolledDice(diceRollEvent.roll)

      // Roll deterministically: pass the server's roll values so each 3D die
      // lands on the exact value the game decided (e.g. [2, 1]).
      const activeDiceRef = isMyTurn ? diceRefSmall : diceRefFull
      if (activeDiceRef.current) {
        activeDiceRef.current.roll(diceRollEvent.roll)
      }
    }
  }, [JSON.stringify(diceRollEvent), myGameId, isMyTurn, preview])
  const map = mapDesigns.find((map) => map.id === mapId)
  const centerLowBrightness = useMemo(() => {
    if (houseBuildMode) return true
    if (boardPickerMode) return true
    if (!activeAuctionPropertyOrder) return false

    return true
  }, [activeAuctionPropertyOrder, houseBuildMode, boardPickerMode])
  return (
    <div
      id="board"
      style={{
        background:
          "linear-gradient(145deg, #f8e5cc 0%, #e6d4b3 50%, #d4c2a1 100%)",
        boxShadow: isMobile() ? "none" : "0 0 20px 10px rgba(0, 0, 0, 0.75)",
      }}
      data-testid="game.board"
      className={classNames("relative aspect-square", {
        // Full bleed: a square as tall as the screen itself, not as tall as the
        // playable box (the screen minus any notch / camera / home-indicator
        // inset). The root grid pads the whole game clear of those insets, so
        // the board cancels the vertical half of that padding back out with
        // negative margins and reaches both physical edges again.
        //
        // Widthwise it simply takes its 100vh: a square that tall is wider than
        // the old --play-h one, and the two player columns are what give up the
        // difference — they are sized as (--play-w - 100vh)/2 (Sidebar /
        // SidebarLeft, and the trade drawers that dock over the left one), so
        // the three columns still add up to --play-w exactly and nothing
        // overlaps the board. Every inset is 0 on a desktop, where this is the
        // plain 100vh square it has always been.
        "h-[100vh] w-[100vh] mt-[calc(var(--safe-top)_*_-1)] mb-[calc(var(--safe-bottom)_*_-1)]":
          !isPortrait,
        "lg:max-http://167.99.229.16:5000/w-[calc(100vw-400px-40px)] aspect-square":
          true,
        [`board-${map?.id}`]: true,
      })}
    >
      {constructionBanned && (
        <div className="absolute top-4 right-4 bg-red-500/90 text-white px-3 py-1.5 rounded-full flex items-center gap-2 font-poppins shadow-lg text-sm">
          <WrenchIcon className="h-6 w-6" />
          <span>{turnRemainingForConstructionBan}</span>
        </div>
      )}

      <BoardPickerBanner />

      {mapDesign!.cells.map((cell) => (
        <BoardCellWrapper key={cell.order} cell={cell}></BoardCellWrapper>
      ))}

      <div
        id="center"
        className={classNames("bg-[#f8e5cc] w-full h-full  relative  ", {
          "brightness-[0.3]": centerLowBrightness,
        })}
      >
        {/* A finished roll, standing still, where the physics dice come to rest.
            The real ones are a three.js canvas that only exists mid-roll, so a
            picture of the board has to draw the result instead. */}
        {preview ? (
          <div className="absolute left-[60%] top-[44%] -translate-x-1/2 -translate-y-1/2 flex items-center gap-4">
            <DieFace value={5} />
            <DieFace value={2} />
          </div>
        ) : (
          <>
            <div
              id="dice-box-small"
              className={classNames("absolute origin-center", {
                "opacity-0": !isMyTurn,
              })}
            ></div>
            <div
              id="dice-box-full"
              className={classNames("absolute origin-center w-full h-full", {
                "opacity-0": isMyTurn,
              })}
            ></div>
          </>
        )}
        <div className="absolute top-8 left-1/2 -translate-x-1/2 px-2 w-full">
          <GameButtonsTop />
        </div>
        <MoneyOnTable />
        <div className="absolute flex flex-col items-center gap-3 sm:bottom-10 bottom-20 left-1/2 -translate-x-1/2 px-2 w-full">
          {/* Once the game is over GameButtons renders nothing, and the slot is
              taken over by the button that re-opens the end-of-game screen. */}
          {gameOver ? <GameEndStatsButton /> : <GameButtons></GameButtons>}
          {/* A pending decision freezes the game for everyone waiting on it, so
              it takes over the "[player] is playing" slot — same place, same
              height (its small second line is absolutely positioned so this
              bottom-anchored column never grows and lifts the buttons). Not for
              the player being asked — they have the modal. */}
          {showDecisionWait && <DecisionWaitBanner />}
          {/* `turn` still names whoever was last up after the game ends, so the
              "is playing" label has to be gated on the game still running. */}
          {!showDecisionWait && !isMyTurn && !gameOver && activePlayer && (
            <div
              key={currentTurn}
              className="now-playing-enter flex items-center gap-2.5 sm:gap-3 mt-6 sm:mt-9"
            >
              {activePiece?.imgSrc && (
                <img
                  src={activePiece.imgSrc}
                  className="h-9 w-9 sm:h-11 sm:w-11 object-contain drop-shadow-[0_1px_1px_rgba(255,255,255,0.5)]"
                />
              )}
              <span className="font-poppins text-2xl sm:text-3xl tracking-wide whitespace-nowrap drop-shadow-[0_1px_0_rgba(255,255,255,0.45)]">
                <span className="font-bold text-[#3d2408] inline-block border-b-2 border-[#d89529] pb-0.5">
                  {activeUser?.username || "Player"}
                </span>
                <span className="font-medium text-[#8a5a2b]">
                  {" "}
                  is playing<span className="now-playing-ellipsis"></span>
                </span>
              </span>
            </div>
          )}
          {/* Idle warning — shown to everyone, so the rest of the table can see
              a stalled player is on a clock rather than reading the still board
              as a crash. Says which move is about to be taken for them (an
              unrolled turn gets rolled, an already-rolled one gets ended), and
              always describes whoever's turn it is, so it hangs off the bottom
              of this column right under the "[player] is playing" label naming
              them.

              Absolutely positioned on purpose: this column is bottom-anchored,
              so anything added to the flow grows it upward and shoves the
              buttons with it — the same reason DecisionWaitBanner's second line
              is absolute. Turns red for the last few seconds. */}
          {idleWarning && (
            // The centering lives on this wrapper, not on the badge: the badge's
            // pulse animates `transform`, which would otherwise overwrite a
            // -translate-x-1/2 on the same element and shunt it sideways on
            // every beat.
            <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2">
              <div
                role="status"
                data-testid="game.auto-end-turn-badge"
                className={classNames(
                  "auto-end-turn-badge flex items-center gap-2 sm:gap-2.5 rounded-full px-3 sm:px-4 py-1.5 sm:py-2 font-poppins shadow-lg ring-1 backdrop-blur-sm whitespace-nowrap",
                  idleWarning.secondsLeft <= 3
                    ? "bg-[#5c1616]/90 ring-[#ff8a6b]/60 auto-end-turn-badge-urgent"
                    : "bg-[#3d2408]/90 ring-[#f0b45c]/60"
                )}
              >
                <i
                  className={classNames(
                    "fa-solid text-sm sm:text-base",
                    idleWarning.action === "roll"
                      ? "fa-dice"
                      : "fa-hourglass-half",
                    idleWarning.secondsLeft <= 3
                      ? "text-[#ff9c7d]"
                      : "text-[#f0b45c]"
                  )}
                ></i>
                <span className="text-sm sm:text-base font-medium text-white/90">
                  {isIdlePlayerMe
                    ? idleWarning.action === "roll"
                      ? "Rolling for you in "
                      : "Turn auto-ends in "
                    : `${idleUser?.username || "Player"} is idle — ${
                        idleWarning.action === "roll"
                          ? "rolling in "
                          : "turn ends in "
                      }`}
                  <span
                    className={classNames(
                      "font-bold tabular-nums",
                      idleWarning.secondsLeft <= 3
                        ? "text-[#ffb3a0]"
                        : "text-[#ffcf7a]"
                    )}
                  >
                    {idleWarning.secondsLeft}s
                  </span>
                </span>
              </div>
            </div>
          )}
        </div>
        {/* Turn clock + turns-left, stacked in the top-right corner of the
            center square. Both are "how much time is left" info, and the corner
            keeps them out of the dice, which tumble all over the middle. */}
        <div className="absolute top-4 right-4 z-10 flex flex-col items-end gap-2">
          <div
            data-testid="game.turn-timer"
            className="rounded-full bg-black/20 p-2 ring-1 ring-white/20 shadow-lg backdrop-blur-sm"
          >
            <GameTimer
              timerKey="turnTimeLeft"
              totalTime={turnTotalTime}
              fallback={0}
              size="2lg"
              showLabel={false}
            />
          </div>
          <TurnsLeftBadge />
        </div>
        {/* Game log — centered, plain text, no borders or boxes.
            On short screens (phones, which the game locks to landscape) the
            root font-size drops to 8px, so a rem width like w-40 collapses to
            ~80px and every entry wraps after a couple of words. The right edge
            is anchored to the board instead, so the log takes the whole empty
            span between the chance stack and the board's right column and
            scales with the board rather than with the font size. */}
        <div className="absolute sm:h-44    h-[calc(13rem)]  top-1/2 -translate-y-1/2 left-[30%]  overflow-hidden">
          <GameLogPanel />
        </div>
        {/* {state.myGame?.situation?.turn === getUserCreds().email && ( */}
        {/*   <div className="absolute flex-col bg-transparent gap-4 z-20 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-black bg-opacity-100 w-full h-full flex items-center justify-center p-20"> */}
        {/*     <DiceArea></DiceArea> */}
        {/*     <GameButtons></GameButtons> */}
        {/*   </div> */}
        {/* )} */}
        {/* <img
          src={centerLogo}
          className={classNames(
            "top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 absolute w-2/4 h-2/4 object-cover"
          )}
        ></img> */}

        <div className="absolute top-1/2 -translate-y-1/2   left-[5%]  sm:h-28 sm:w-40   h-[calc(9rem)] w-[calc(13rem)]  md:opacity-100 opacity ">
          <div className="-rotate-90 border  h-full w-full absolute border-black   border-dashed bg-contain"></div>
          <div className="sm:h-24 sm:w-36 h-[8rem] w-[12rem] absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 ">
            {map?.allChances.map((chance, i) => (
              <Chance index={i} isChance chance={chance}></Chance>
            ))}
          </div>
        </div>
        {/* <div className="absolute top-1/2 -translate-y-1/2  -right-[0%]  h-28 w-44 md:h-36 md:w-60 md:opacity-100 opacity">
          <div className="-rotate-90 border  h-full w-full absolute border-black  border-dashed bg-contain"></div>
          <div className="h-[calc(100%-1.25rem)] w-[calc(100%-1.25rem)] absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
            {map?.allCommunityChests.map((chance, i) => (
              <Chance index={i} chance={chance}></Chance>
            ))}
          </div>
        </div> */}
      </div>
    </div>
  )
}

export default memo(Board)
